import type { FeedTypes } from "@/domain/gems-game/data-access/lib/discover-feed-types";
import type { LinkedAccountPlatform } from "@/utils/linkedaccounts/linkedAccounts";
import { notifyError } from "@/utils/monitoring/newrelic";
import { convertFieldDataToTemplate } from "./DiscoverFeedTemplateConverter";
import type { VideoUrl } from "./VideoUrl";

export enum QuizHeaderToCMSType {
  Image = "92ad1b2f6fa1e1bdbf2a7d685469d10e",
  Video = "814cd153ee5bedf9795cb98c8ebf3bb0",
}

export interface BaseFieldData {
  "quest-type": string;
  name: string; // item id
  slug: string; // quest id (legacy - use quest-id when available)
  "quest-id"?: string; // quest id (preferred over slug)
  "gems-earnable-2": number;
  priority: number;
  "quest-complete-popup-text"?: string;
  bypass: boolean;
  "onboarding-experience"?: string;
  categories?: string; // Comma-separated list of categories
  tags?: string; // Comma-separated list of tags
  "control-tags"?: string; // Comma-separated list of tags for filtering/control logic
  createdAt?: Date;
  "experiment-id-2"?: string;
  "experiment-variant"?: string;
}

export interface RewardFieldData extends BaseFieldData {
  "cta-url"?: string;
  "cta-label"?: string;
  "cta-description"?: string;
  "reward-background": string;
  "reward-signin-redirect-url"?: string;
}

export interface CTAFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "cta-image-url": string;
  "cta-url"?: string;
  "cta-label"?: string;
  "cta-description"?: string;
  "video-title"?: string; // hack - reuse the video title property for CTA title
}

export interface FollowGameFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "cta-image-url": string;
  "cta-url"?: string;
  "cta-label"?: string;
  "cta-description"?: string;
  "video-title"?: string; // hack - reuse the video title property for follow game title
  "quiz-logo-url-2"?: string; // hack - reuse the quiz logo url property for game logo
  "similar-games"?: string; // Comma-separated list of similar games
}

export interface SocialQuestFieldData extends BaseFieldData {
  "game-id": string; // game.id
  "game-name": string; // game.name
  "cta-image-url": string; // backgroundImageUrl
  "video-title": string; // title
  "sub-title": string; // subTitle -> new field
  "cta-label": string; // quest caption
  "cta-description": string; // description
  "quiz-logo-url-2": string; // gameLogoUrl
  "platform-name": string; // quest.platform -> new field
  "cta-url": string; // quest.linkUrl
}

export interface InGameQuestFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "cta-image-url"?: string; // backgroundImage
  "cta-url"?: string; // action URL
  "cta-label"?: string; // button label
  "cta-description"?: string; // description
  "video-title"?: string; // title
}

export interface VideoFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "video-title"?: string;
  "cta-url"?: string;
  "cta-label"?: string;
  "video-url"?: string;
  "quiz-logo-url-2"?: string;
}

export interface QuizFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "quiz-header-url": string;
  "quiz-header-initial-image-url"?: string;
  "quiz-header-answered-image-url"?: string;
  "quiz-answers"?: string;
  "quiz-correct-answers"?: string;
  "quiz-correct-answer-message"?: string;
  "quiz-header-type"?: QuizHeaderToCMSType.Video | QuizHeaderToCMSType.Image;
  "quiz-logo-url-2"?: string;
  "quiz-question"?: string;
}

export type DiscoverFeedFieldData =
  | VideoFieldData
  | QuizFieldData
  | CTAFieldData
  | FollowGameFieldData
  | RewardFieldData
  | SocialQuestFieldData
  | InGameQuestFieldData
  | BaseFieldData;

export type Game = {
  id: string; // e.g. 1234-125151-125-1-51-5 - used for following. TODO: Where do we get this from?
  name: string; // e.g. Dark Machines
};

export type CTA = {
  label?: string;
  description: string; // e.g. "Find out more"
  url: string; // e.g. https://mycoolgame.com
};

export type QuizHeader =
  | {
      url: VideoUrl | undefined;
      type: "video";
    }
  | {
      initialImage: URL | undefined;
      answeredImage: URL | undefined;
      type: "image";
    };

export type FeedQuest = {
  questId: string;
  gemsEarnable: number;
};

export type FeedABQuest = FeedQuest & {
  platform: "x" | "discord";
  linkUrl?: string | undefined;
};

export type BaseTemplate = {
  id: string;
  quest: FeedQuest;
  priority: number;
  questCompletePopupText?: string;
  bypass: boolean;
  onboardingExperience?: string;
  categories: string[];
  tags: string[]; // For UI display
  controlTags?: string[];
  createdAt?: Date;
  type?: TemplateTypes;
  experimentId?: string;
  experimentVariant?: string;
};

export type CTATemplate = BaseTemplate & {
  type: "cta";
  image: URL;
  game: Game;
  title: string | undefined;
  description: string | undefined;
  buttonLabel: string | undefined;
  buttonURL: string | undefined;
};

export type RewardTemplate = BaseTemplate & {
  type: "reward" | "onboardingsignin";
  rewardBackground: URL | undefined;
  cta: CTA | undefined;
  rewardSigninRedirectUrl: URL | undefined;
};

export type VideoTemplate = BaseTemplate & {
  game: Game;

  // Video specific reqs
  type: "video"; // Discriminator
  title: string | undefined; // e.g. "We think you'll like"
  videoUrl: VideoUrl;
  cta: CTA | undefined;
  logoUrl: URL | string | undefined;

  quest: FeedQuest;
};

export type QuizTemplate = BaseTemplate & {
  game: Game;

  // Quiz specific reqs
  type: "quiz"; // Discriminator
  logoUrl: URL | undefined;
  header: QuizHeader;
  question: string;
  answers: string[];
  correctAnswers: number[]; // TODO: Does not prevent FE hax to get the right answers. Suggestion: update BE to validate the answers.
  correctAnswerMessage?: string;
};

export type PinToHomeTemplate = BaseTemplate & {
  type: "pintohome";
};

export type AllCaughtUpTemplate = BaseTemplate & {
  type: "allcaughtup";
};

export type SelectQuestDifficultyTemplate = BaseTemplate & {
  type: "selectquestdifficulty";
};

export type SocialConnectionTemplate = BaseTemplate & {
  type: "socialconnection";
  platform: LinkedAccountPlatform;
};

export type SignInTemplate = BaseTemplate & {
  type: "signin";
};

export type BonusGemsTemplate = BaseTemplate & {
  type: "bonusgems";
};

export type ReferralTemplate = BaseTemplate & {
  type: "referral";
};

export type FollowGameTemplate = BaseTemplate & {
  type: "followgame";
  image: string;
  game: Game;
  title: string | undefined;
  description: string | undefined;
  buttonLabel: string | undefined;
  gameLogoUrl: string | undefined;
  categories: string[];
  similarGames: string[];
};

export type GetRewardsReadyTemplate = BaseTemplate & {
  type: "getrewardsready";
};

export type SocialQuestTemplate = BaseTemplate & {
  type: "socialquest";
  backgroundImageUrl: string;
  questCaption: string;
  title: string;
  subTitle: string;
  description: string;
  gameLogoUrl: string;
  categories: string[];
  quest: FeedABQuest;
  game: Game;
};

export type SelectGenreTemplate = BaseTemplate & {
  type: "selectgenre";
};

export type InGameQuestTemplate = BaseTemplate & {
  type: "ingamequest";
  game: Game;
  title?: string;
  description?: string;
  buttonLabel?: string;
  buttonURL?: string;
  backgroundImage?: string;
  gameLogoUrl?: string;
  infoItems?: string[];
};

export enum DiscoverActivateStep {
  WELCOME = "welcome",
  MONEY = "money",
  GAMER_TYPE = "gamer_type",
  SOCIAL_QUEST = "social_quest",
  VERIFY_PHONE = "verify_phone",
  IN_GAME_QUEST = "in_game_quest",
  TUTORIAL_QUEST = "tutorial_quest",
  ADVENTURE_DUNGEONS = "adventure_dungeons",
  CONGRATS = "congrats",
  MONEY_CONGRATS = "money_congrats",
}

export type DiscoverFeedItem =
  | VideoTemplate
  | QuizTemplate
  | CTATemplate
  | RewardTemplate
  | PinToHomeTemplate
  | AllCaughtUpTemplate
  | SelectQuestDifficultyTemplate
  | SocialConnectionTemplate
  | BonusGemsTemplate
  | ReferralTemplate
  | SignInTemplate
  | FollowGameTemplate
  | GetRewardsReadyTemplate
  | SocialQuestTemplate
  | SelectGenreTemplate
  | InGameQuestTemplate
  | undefined;
export type DiscoverFeed = DiscoverFeedItem[];

export type TemplateTypes =
  | "video"
  | "quiz"
  | "cta"
  | "reward"
  | "onboardingsignin" // This will replace the `reward` type once we have migrated all content to the new `onboardingsignin` type
  | "pintohome"
  | "allcaughtup"
  | "selectquestdifficulty"
  | "socialconnection"
  | "signin"
  | "bonusgems"
  | "referral"
  | "followgame"
  | "getrewardsready"
  | "socialquest"
  | "selectgenre"
  | "ingamequest";

export const getDiscoverFeedItemFromFieldData = (
  fieldData: DiscoverFeedFieldData,
  feedTypes: FeedTypes,
): DiscoverFeedItem => {
  const questType = fieldData["quest-type"];
  if (!questType) {
    notifyError(
      "Missing quest type in field data",
      "discoverFeedError",
      fieldData,
    );
    return undefined;
  }

  // Use the CMS types to determine the template type
  const questTypeName = feedTypes.questTypes[questType];
  if (!questTypeName) {
    notifyError(
      `Unknown quest type: ${questType}`,
      "discoverFeedError",
      fieldData,
    );
    return undefined;
  }

  return convertFieldDataToTemplate(questTypeName as TemplateTypes, fieldData);
};
